'use client'

import React from 'react'
import LMPhone from './Icons/LMPhone'
import LMEmail from './Icons/LMEmail'
import { BadgeCheck } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useBooking } from '@/providers/BookingContext'
import { EmailObfuscator } from './EmailObfuscator'

type IconsDataType = {
  isOverview: boolean
}

const Sidebar: React.FC<IconsDataType> = () => {
  const { setIsSheetOpen, setSource } = useBooking()
  const pathname = usePathname()

  const segments = pathname?.split('/').filter(Boolean) || []
  const isTourPage =
    segments.length >= 2 &&
    (segments[0] === 'bike-tours' || segments[0] === 'holiday-packages')

  const tourType = segments[0]
  const slug = isTourPage ? segments[segments.length - 1] : null

  // Determine correct PDF directory based on tour type
  const pdfDir = tourType === 'bike-tours' ? 'bike-tours' : 'holiday-packages'
  const pdfUrl = slug ? `/downloads/${pdfDir}/${slug}.pdf` : '#'
  const downloadName = slug ? slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('-') : 'Itinerary'

  return (
    <div id='sidebar' className='mb-8' style={{position: 'sticky', top: '110px'}}>
      <hr className='my-8 lg:hidden mb-26 border-border/20' />

      {/* Unified Booking & Support Console */}
      <div className="bg-white/70 dark:bg-[#0b1329]/45 backdrop-blur-xl border border-black/5 dark:border-white/5 rounded-3xl shadow-xl p-6 space-y-6 relative overflow-hidden group/sidebar transition-all duration-300 hover:shadow-2xl hover:shadow-primary/5">
        {/* Subtle decorative glow */}
        <div className="absolute -right-24 -top-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl group-hover/sidebar:bg-primary/15 transition-all duration-700" />
        
        {/* SECTION 1: Booking CTA */}
        <div className="space-y-4 relative z-1">
          <div className="flex justify-between items-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary">Flexible Booking</span>
            </div>
          </div>

          <p className="text-xs text-foreground/90 font-medium leading-relaxed px-1">
            Secure your adventure with a deposit of <span className="text-primary font-semibold underline decoration-primary/30 decoration-2 underline-offset-4">30%</span> today.
          </p>

          <div className="space-y-2.5">
            <button
              id="booking-sidebar"
              onClick={() => {
                setSource('sidebar')
                setIsSheetOpen(true)
              }}
              title="Book Now"
              className="w-full font-oswald text-white text-center font-medium bg-primary hover:bg-primary/95 transition-all duration-300 rounded-xl h-11 flex items-center justify-center shadow-md hover:shadow-primary/20 uppercase tracking-widest cursor-pointer group/btn"
            >
              <span className="text-xs flex items-center gap-2">
                Book Now 
                <svg className="w-3.5 h-3.5 transform group-hover/btn:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </button>

            {isTourPage ? (
              <Link
                id="download-itinerary"
                href={pdfUrl}
                download={downloadName}
                type="application/octet-stream"
                aria-label={`Download ${downloadName} Itinerary PDF`}
                title={`Download ${downloadName} Itinerary PDF`}
                target="_blank"
                className="w-full font-oswald text-foreground text-center font-normal border border-border hover:border-primary hover:bg-primary/5 transition-all duration-300 rounded-xl h-11 flex items-center justify-center uppercase tracking-widest"
              >
                <i className="bi bi-download mr-2 text-xs"></i>
                <span className="text-xs">
                  Itinerary PDF
                </span>
              </Link>
            ) : (
              <Link
                id="view-tours"
                href="/bike-tours"
                className="w-full font-oswald text-foreground text-center font-normal border border-border hover:border-primary hover:bg-primary/5 transition-all duration-300 rounded-xl h-11 flex items-center justify-center uppercase tracking-widest"
              >
                <span className="text-xs">
                  View Tours
                </span>
              </Link>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-linear-to-r from-transparent via-border/50 to-transparent relative z-1"></div>

        {/* SECTION 2: Trust Advantages */}
        <div className="space-y-4 relative z-1">
          <h3 className="font-oswald text-xs font-semibold text-foreground uppercase tracking-[3px] flex items-center gap-2.5">
            <span className="w-1 h-3.5 bg-primary rounded-full"></span>
            Why Book With Us?
          </h3>
          
          <ul className="space-y-1">
            {[
              "No-Hassle, Best Price Guarantee",
              "Local Himalayan Operator",
              "Backup Vehicles & Support",
              "Hand-Picked Tours & Routes",
              "RE Himalayan Bike With Fuel",
              "Handpicked Accommodation",
              "Includes Lodging, Meal & Fuel",
              "Oxygen & Mechanic Support",
              "Customer Care Available 24/7"
            ].map((benefit, index) => (
              <li 
                key={index} 
                className="group flex items-start gap-2.5 text-xs text-muted-foreground hover:text-foreground hover:translate-x-0.5 transition-all duration-200 py-1.5 border-b border-black/5 dark:border-white/5 last:border-0"
              >
                <BadgeCheck className="text-green-400 shrink-0 size-4 mt-0.5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300" strokeWidth={1} />
                <span className="font-light leading-snug">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Divider */}
        <div className="h-px bg-linear-to-r from-transparent via-border/50 to-transparent relative z-1"></div>

        {/* SECTION 3: Traveler Support */}
        <div className="space-y-4 relative z-1">
          <div className="space-y-1.5">
            <h3 className="font-oswald text-xs font-semibold text-foreground uppercase tracking-[3px] flex items-center gap-2.5">
              <span className="w-1 h-3.5 bg-primary rounded-full"></span>
              Traveler Support
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed px-1">
              Need assistance? Connect with our local Ladakh expert team directly.
            </p>
          </div>
          
          <div className="space-y-2.5">
            <a 
              href="tel:+91-9622958013" 
              aria-label="Phone One" 
              title="Phone One" 
              className="group flex items-center gap-3 p-2.5 rounded-xl border border-black/5 dark:border-white/5 hover:border-primary/30 dark:hover:border-primary/20 hover:bg-primary/5 transition-all duration-300 cursor-pointer"
            >
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:bg-primary transition-all duration-300">
                <LMPhone className="size-4 text-primary group-hover:text-white transition-colors duration-300" />
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">Call Expert</span>
                <span className="text-xs text-foreground font-mono font-semibold group-hover:text-primary transition-colors duration-300">+91-9622958013</span>
              </div>
            </a>

            <a 
              href="tel:+91-9906989962" 
              aria-label="Phone Two" 
              title="Phone Two" 
              className="group flex items-center gap-3 p-2.5 rounded-xl border border-black/5 dark:border-white/5 hover:border-primary/30 dark:hover:border-primary/20 hover:bg-primary/5 transition-all duration-300 cursor-pointer"
            >
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:bg-primary transition-all duration-300">
                <LMPhone className="size-4 text-primary group-hover:text-white transition-colors duration-300" />
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">Backup Line</span>
                <span className="text-xs text-foreground font-mono font-semibold group-hover:text-primary transition-colors duration-300">+91-9906989962</span>
              </div>
            </a>

            <div className="group flex items-center gap-3 p-2.5 rounded-xl border border-black/5 dark:border-white/5 hover:border-primary/30 dark:hover:border-primary/20 hover:bg-primary/5 transition-all duration-300 cursor-pointer">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:bg-primary transition-all duration-300">
                <LMEmail className="size-4 text-primary group-hover:text-white transition-colors duration-300" />
              </div>
              <div className="flex flex-col w-full">
                <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">Email Us</span>
                <EmailObfuscator
                  email="info@marmotexpedition.com"
                  className="text-xs text-foreground font-semibold group-hover:text-primary transition-colors duration-300 block"
                  showEmail={false}
                >
                  info@marmotexpedition.com
                </EmailObfuscator>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Sidebar
